import React, { useEffect, useRef, useState } from "react";
import Menubar from "../Menubar/Menubar";
import { Outlet } from "react-router-dom";
import "../Dashboard/Dashboard.css";

import {
  ImagePlus,
  Monitor,
  Smartphone,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Link as LinkIcon,
  X,
  Pencil,
  Eye,
  ExternalLink,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_API_URL || "https://dash.zorbastays.com/web-backend"; // your backend base URL

// map DB row -> UI banner
const fromRow = (row) => ({
  id: row.id,
  desktopImage: row.bannerimagedes || "",
  mobileImage: row.bannerimagemob || "",
  link: row.bannerlink || "",
  desktopFile: null,
  mobileFile: null,
  removeDesktop: false,
  removeMobile: false,
  isNew: false,
});

export default function BannerElementor() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    return savedState !== null
      ? JSON.parse(savedState)
      : window.innerWidth >= 1024;
  });

  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [previewMobile, setPreviewMobile] = useState(window.innerWidth < 640);

  const carouselRef = useRef(null);
  const previewUrls = useRef(new Set());

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      setIsMobile(width < 1024);
      setPreviewMobile(width < 640);
      if (width < 1024) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    return () => {
      previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.current.clear();
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/banners`);
        const json = await res.json();
        if (json.success) setBanners(json.data.map(fromRow));
      } catch (err) {
        console.error("Failed to load banners", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeBanner = banners[activeIndex];
  const editingBanner = banners.find((banner) => banner.id === editingId);
  const closeEditor = () => {
    if (editingBanner?.isNew) {
      // discard unsaved new slide
      setBanners((current) => current.filter((b) => b.id !== editingBanner.id));
      setActiveIndex((i) => Math.max(0, Math.min(i, banners.length - 2)));
    }
    setEditingId(null);
  };
  const updateBanner = (id, changes) => {
    setBanners((current) =>
      current.map((banner) =>
        banner.id === id ? { ...banner, ...changes } : banner,
      ),
    );
  };

  const selectImage = (id, type, file) => {
    if (!file) return;
    if (file.type !== "image/webp") {
      alert("Only WebP images are allowed.");
      return;
    }

    const isDesktop = type === "desktop";
    const imgKey = isDesktop ? "desktopImage" : "mobileImage";
    const fileKey = isDesktop ? "desktopFile" : "mobileFile";
    const removeKey = isDesktop ? "removeDesktop" : "removeMobile";
    const previewUrl = URL.createObjectURL(file);
    previewUrls.current.add(previewUrl);

    setBanners((current) =>
      current.map((banner) => {
        if (banner.id !== id) return banner;
        const old = banner[imgKey];
        if (old?.startsWith("blob:")) {
          URL.revokeObjectURL(old);
          previewUrls.current.delete(old);
        }
        return {
          ...banner,
          [imgKey]: previewUrl,
          [fileKey]: file,
          [removeKey]: false,
        };
      }),
    );
  };

  const removeImage = (id, type) => {
    const isDesktop = type === "desktop";
    const imgKey = isDesktop ? "desktopImage" : "mobileImage";
    const fileKey = isDesktop ? "desktopFile" : "mobileFile";
    const removeKey = isDesktop ? "removeDesktop" : "removeMobile";

    setBanners((current) =>
      current.map((banner) => {
        if (banner.id !== id) return banner;
        const old = banner[imgKey];
        if (old?.startsWith("blob:")) {
          URL.revokeObjectURL(old);
          previewUrls.current.delete(old);
        }
        return { ...banner, [imgKey]: "", [fileKey]: null, [removeKey]: true };
      }),
    );
  };
  const addBanner = () => {
    const newBanner = {
      id: Date.now() + Math.random(), // temporary client id
      desktopImage: "",
      mobileImage: "",
      link: "",
      desktopFile: null,
      mobileFile: null,
      removeDesktop: false,
      removeMobile: false,
      isNew: true,
    };
    setBanners((current) => [...current, newBanner]);
    setActiveIndex(banners.length);
    setEditingId(newBanner.id);
  };

  const selectBanner = (index) => {
    setActiveIndex(index);
  };

  const saveBanner = async (banner) => {
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("bannerlink", banner.link || "");
      if (banner.desktopFile) fd.append("desktopImage", banner.desktopFile);
      if (banner.mobileFile) fd.append("mobileImage", banner.mobileFile);
      if (banner.removeDesktop) fd.append("removeDesktop", "true");
      if (banner.removeMobile) fd.append("removeMobile", "true");

      const res = await fetch(
        banner.isNew
          ? `${API_BASE}/banners`
          : `${API_BASE}/banners/${banner.id}`,
        {
          method: banner.isNew ? "POST" : "PUT",

          body: fd,
        },
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Save failed");

      // free blob previews, swap in the saved row (real id + server URLs)
      [banner.desktopImage, banner.mobileImage].forEach((u) => {
        if (u?.startsWith("blob:")) {
          URL.revokeObjectURL(u);
          previewUrls.current.delete(u);
        }
      });
      setBanners((current) =>
        current.map((b) => (b.id === banner.id ? fromRow(json.data) : b)),
      );
      setEditingId(null);
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save banner");
    } finally {
      setSaving(false);
    }
  };

  const deleteBanner = async (id) => {
    const banner = banners.find((item) => item.id === id);
    if (!banner) return;
    if (!window.confirm("Delete this banner?")) return;

    if (!banner.isNew) {
      try {
        const res = await fetch(`${API_BASE}/banners/${id}`, {
          method: "DELETE",
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
      } catch (err) {
        alert(err.message || "Failed to delete banner");
        return;
      }
    }

    [banner.desktopImage, banner.mobileImage].forEach((url) => {
      if (url?.startsWith("blob:")) {
        URL.revokeObjectURL(url);
        previewUrls.current.delete(url);
      }
    });

    const updated = banners.filter((item) => item.id !== id);
    setBanners(updated);
    setActiveIndex(Math.max(0, Math.min(activeIndex, updated.length - 1)));
    if (editingId === id) setEditingId(null);
  };

  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;

    carouselRef.current.scrollBy({
      left: direction * 260,
      behavior: "smooth",
    });
  };

  const imageForPreview = (banner) => {
    if (!banner) return "";
    return previewMobile
      ? banner.mobileImage || banner.desktopImage
      : banner.desktopImage || banner.mobileImage;
  };

  const UploadBox = ({ banner, type, title }) => {
    const inputRef = useRef(null);
    const isDesktop = type === "desktop";
    const key = isDesktop ? "desktopImage" : "mobileImage";
    const image = banner[key];

    return (
      <div className="min-w-0">
        <div className="mb-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            {isDesktop ? <Monitor size={17} /> : <Smartphone size={17} />}
            {title}
          </label>

          {image && (
            <button
              type="button"
              onClick={() => removeImage(banner.id, type)}
              className="text-xs font-medium text-red-500 hover:text-red-700"
            >
              Remove
            </button>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/webp"
          className="hidden"
          onChange={(event) => {
            selectImage(banner.id, type, event.target.files?.[0]);
            event.target.value = "";
          }}
        />

        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`group relative flex w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition hover:border-gray-500 hover:bg-gray-100 ${
            isDesktop ? "aspect-video" : "aspect-[4/5]"
          }`}
        >
          {image ? (
            <>
              <img
                src={image}
                alt={title}
                className="absolute inset-0 h-full w-full object-cover"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
                <span className="rounded-lg bg-black/60 px-3 py-2 text-sm">
                  Change image
                </span>
              </span>
            </>
          ) : (
            <span className="flex flex-col items-center px-3 text-center">
              <ImagePlus size={28} className="mb-3 text-gray-400" />
              <span className="text-sm font-semibold text-gray-700">
                Upload {isDesktop ? "desktop" : "mobile"} image
              </span>
              <span className="mt-1 text-xs text-gray-400">WebP</span>
            </span>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className="flex min-h-screen bg-[#F8F9FA]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      <main
        className={`w-full min-w-0 p-4 transition-all duration-500 sm:p-6 ${
          isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
        }`}
      >
        <Outlet />

        <div className="mb-7 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div className="ml-10 lg:ml-0">
            <h1 className="text-xl font-bold text-gray-800">
              Banner Elementor
            </h1>
          </div>

          <div className="flex items-center gap-1">{/*  */}</div>
        </div>
        {loading && (
          <p className="mb-4 text-sm text-gray-500">Loading banners...</p>
        )}
        {/* Carousel slide selector */}
        <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-bold text-gray-800">Banner carousel</h2>
              <p className="mt-1 text-sm text-gray-500">
                Select a slide to preview or edit its images and link.
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={() => scrollCarousel(-1)}
                aria-label="Scroll carousel left"
                className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
              >
                <ChevronLeft size={19} />
              </button>
              <button
                type="button"
                onClick={() => scrollCarousel(1)}
                aria-label="Scroll carousel right"
                className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
              >
                <ChevronRight size={19} />
              </button>
            </div>
          </div>

          <div
            ref={carouselRef}
            className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3"
          >
            {banners.map((banner, index) => {
              const thumbnail = imageForPreview(banner);
              const selected = activeBanner?.id === banner.id;

              return (
                <div
                  key={banner.id}
                  className={`w-[220px] shrink-0 snap-start overflow-hidden rounded-xl border-2 transition sm:w-[250px] ${
                    selected
                      ? "border-gray-900 shadow-md"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => selectBanner(index)}
                    className="block w-full text-left"
                  >
                    <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-gray-100">
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={`Banner ${index + 1}`}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="flex flex-col items-center text-gray-400">
                          <ImagePlus size={27} />
                          <span className="mt-2 text-xs">No image yet</span>
                        </div>
                      )}

                      <span className="absolute left-2 top-2 rounded-md bg-black/70 px-2 py-1 text-xs font-semibold text-white">
                        Slide {index + 1}
                      </span>
                    </div>

                    <div className="p-3">
                      <p className="truncate text-sm font-semibold text-gray-800">
                        Banner {index + 1}
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        {banner.desktopImage ? "Desktop ✓" : "Desktop —"}
                        {" · "}
                        {banner.mobileImage ? "Mobile ✓" : "Mobile —"}
                      </p>
                    </div>
                  </button>

                  <div className="flex gap-2 border-t border-gray-100 p-3">
                    <button
                      type="button"
                      onClick={() => {
                        setActiveIndex(index);
                        setEditingId(banner.id);
                      }}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-700"
                    >
                      <Pencil size={14} />
                      Edit slide
                    </button>

                    <button
                      type="button"
                      onClick={() => deleteBanner(banner.id)}
                      aria-label={`Delete banner ${index + 1}`}
                      className="rounded-lg border border-gray-200 p-2 text-red-500 hover:bg-red-50"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              onClick={addBanner}
              className="flex min-h-[220px] w-[180px] shrink-0 snap-start flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 transition hover:border-gray-500 hover:bg-gray-100 sm:w-[200px]"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <Plus size={23} />
              </span>
              <span className="text-sm font-semibold">Add banner</span>
              <span className="mt-1 text-xs">Create a new slide</span>
            </button>
          </div>
        </section>

        {/* Responsive live preview */}
        <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-bold text-gray-800">Live Banner Preview</h2>
              <p className="mt-1 text-sm text-gray-500">
                Preview switches to the mobile image when the screen is narrow.
              </p>
            </div>

            <span className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600">
              {previewMobile ? <Smartphone size={15} /> : <Monitor size={15} />}
              {previewMobile ? "Mobile view" : "Desktop view"}
            </span>
          </div>

          {activeBanner ? (
            <>
              <div
                className={`relative mx-auto overflow-hidden bg-gray-100 ${
                  previewMobile
                    ? "h-screen w-full max-w-[360px]"
                    : "h-screen w-full"
                }`}
              >
                {imageForPreview(activeBanner) ? (
                  <img
                    src={imageForPreview(activeBanner)}
                    alt={`Banner ${activeIndex + 1} preview`}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center text-gray-400">
                    <ImagePlus size={34} />
                    <p className="mt-3 text-sm font-semibold">
                      No {previewMobile ? "mobile" : "desktop"} image selected
                    </p>
                    <p className="mt-1 text-xs">
                      Edit this slide to upload an image.
                    </p>
                  </div>
                )}

                <div className="absolute bottom-3 left-3 rounded-md bg-black/65 px-3 py-1.5 text-xs font-medium text-white">
                  Slide {activeIndex + 1} / {banners.length}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="min-w-0 break-all text-sm text-gray-500">
                  <LinkIcon size={15} className="mr-2 inline" />
                  {activeBanner.link || "No link added"}
                </p>

                <button
                  type="button"
                  onClick={() => setEditingId(activeBanner.id)}
                  className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  <Pencil size={15} />
                  Edit selected slide
                </button>
              </div>
            </>
          ) : (
            <p className="py-12 text-center text-sm text-gray-500">
              Add a banner to start your carousel.
            </p>
          )}
        </section>
      </main>

      {/* Edit slide popup */}
      {editingBanner && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-6"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeEditor();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-banner-title"
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <div>
                <h2 id="edit-banner-title" className="font-bold text-gray-800">
                  Edit banner{" "}
                  {banners.findIndex(
                    (banner) => banner.id === editingBanner.id,
                  ) + 1}
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  Images and link apply only to this slide.
                </p>
              </div>

              <button
                type="button"
                onClick={closeEditor}
                aria-label="Close editor"
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <X size={21} />
              </button>
            </div>

            <div className="overflow-y-auto p-5 sm:p-6">
              <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
                <UploadBox
                  key={`${editingBanner.id}-desktop`}
                  banner={editingBanner}
                  type="desktop"
                  title="Desktop image (16 : 9)"
                />

                <UploadBox
                  key={`${editingBanner.id}-mobile`}
                  banner={editingBanner}
                  type="mobile"
                  title="Mobile image (4 : 5)"
                />
              </div>

              <div className="mt-6">
                <label
                  htmlFor="banner-destination"
                  className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700"
                >
                  <LinkIcon size={16} />
                  Link
                </label>

                <input
                  id="banner-destination"
                  type="text"
                  value={editingBanner.link}
                  onChange={(event) =>
                    updateBanner(editingBanner.id, {
                      link: event.target.value,
                    })
                  }
                  placeholder="/property-details/..."
                  className="w-full rounded-lg border border-gray-300 px-3 py-3 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
                />

                <p className="mt-2 text-xs text-gray-400">
                  Enter the page path or URL users should visit when this banner
                  is clicked.
                </p>
              </div>

              <div className="mt-6 rounded-xl bg-gray-50 p-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <Eye size={16} />
                  Preview for this slide
                </div>

                <div
                  className={`relative mx-auto overflow-hidden rounded-lg bg-gray-200 ${
                    previewMobile
                      ? "h-[70vh] w-full max-w-[280px]"
                      : "h-[70vh] w-full"
                  }`}
                >
                  {imageForPreview(editingBanner) ? (
                    <img
                      src={imageForPreview(editingBanner)}
                      alt="Selected banner"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
                      {previewMobile ? (
                        <Smartphone size={24} />
                      ) : (
                        <Monitor size={24} />
                      )}
                      <span className="mt-2 text-xs">
                        Select an image to preview
                      </span>
                    </div>
                  )}
                </div>

                <p className="mt-3 text-xs text-gray-500">
                  Current preview: {previewMobile ? "Mobile" : "Desktop"}.
                  Resize the browser to check the responsive image.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={closeEditor}
                className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={() => saveBanner(editingBanner)}
                className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
//=================================Working Code=================================
// import React, { useEffect, useRef, useState } from "react";
// import Menubar from "../Menubar/Menubar";
// import { Outlet } from "react-router-dom";
// import "../Dashboard/Dashboard.css";

// import {
//   ImagePlus,
//   Monitor,
//   Smartphone,
//   Plus,
//   Trash2,
//   ChevronLeft,
//   ChevronRight,
//   Link as LinkIcon,
//   X,
//   Pencil,
//   Eye,
//   ExternalLink,
// } from "lucide-react";

// const initialBanners = [
//   {
//     id: 1,
//     desktopImage: "",
//     mobileImage: "",
//     link: "/property-details/923/villa/house-of-t-o/goa/parra/guest=12/bedroom=5/bathroom=6",
//   },
//   {
//     id: 2,
//     desktopImage: "",
//     mobileImage: "",
//     link: "/property-details/1107/villa/villa-serai-unit-2-bianca/goa/vagator/guest=10/bedroom=4/bathroom=5",
//   },
//   {
//     id: 3,
//     desktopImage: "",
//     mobileImage: "",
//     link: "/property-details/34/villa/house-of-basho/goa/arpora/guest=20/bedroom=8/bathroom=10",
//   },
//   {
//     id: 4,
//     desktopImage: "",
//     mobileImage: "",
//     link: "/property-details/116/villa/villa-dreamer-v/goa/assagao/guest=10/bedroom=5/bathroom=5",
//   },
//   {
//     id: 5,
//     desktopImage: "",
//     mobileImage: "",
//     link: "/property-details/1269/villa/villa-ulu/goa/anjuna/guest=10/bedroom=3/bathroom=3",
//   },
// ];

// export default function BannerElementor() {
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const savedState = localStorage.getItem("menubarOpen");

//     return savedState !== null
//       ? JSON.parse(savedState)
//       : window.innerWidth >= 1024;
//   });

//   const [banners, setBanners] = useState(initialBanners);
//   const [activeIndex, setActiveIndex] = useState(0);
//   const [editingId, setEditingId] = useState(null);
//   const [previewMobile, setPreviewMobile] = useState(window.innerWidth < 640);

//   const carouselRef = useRef(null);
//   const previewUrls = useRef(new Set());

//   useEffect(() => {
//     const handleResize = () => {
//       const width = window.innerWidth;

//       setIsMobile(width < 1024);
//       setPreviewMobile(width < 640);
//       if (width < 1024) {
//         setIsSidebarOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);

//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   useEffect(() => {
//     return () => {
//       previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
//       previewUrls.current.clear();
//     };
//   }, []);

//   const activeBanner = banners[activeIndex];
//   const editingBanner = banners.find((banner) => banner.id === editingId);

//   const updateBanner = (id, changes) => {
//     setBanners((current) =>
//       current.map((banner) =>
//         banner.id === id ? { ...banner, ...changes } : banner,
//       ),
//     );
//   };

//   const selectImage = (id, type, file) => {
//     if (!file) return;

//     if (!file.type.startsWith("image/")) {
//       alert("Please select a valid image file.");
//       return;
//     }

//     const key = type === "desktop" ? "desktopImage" : "mobileImage";
//     const previewUrl = URL.createObjectURL(file);

//     previewUrls.current.add(previewUrl);

//     setBanners((current) =>
//       current.map((banner) => {
//         if (banner.id !== id) return banner;

//         const oldImage = banner[key];

//         if (oldImage?.startsWith("blob:")) {
//           URL.revokeObjectURL(oldImage);
//           previewUrls.current.delete(oldImage);
//         }

//         return { ...banner, [key]: previewUrl };
//       }),
//     );
//   };

//   const removeImage = (id, type) => {
//     const key = type === "desktop" ? "desktopImage" : "mobileImage";

//     setBanners((current) =>
//       current.map((banner) => {
//         if (banner.id !== id) return banner;

//         const oldImage = banner[key];

//         if (oldImage?.startsWith("blob:")) {
//           URL.revokeObjectURL(oldImage);
//           previewUrls.current.delete(oldImage);
//         }

//         return { ...banner, [key]: "" };
//       }),
//     );
//   };

//   const addBanner = () => {
//     const newBanner = {
//       id: Date.now() + Math.random(),
//       desktopImage: "",
//       mobileImage: "",
//       link: "",
//     };

//     setBanners((current) => [...current, newBanner]);
//     setActiveIndex(banners.length);
//     setEditingId(newBanner.id);
//   };

//   const deleteBanner = (id) => {
//     const banner = banners.find((item) => item.id === id);

//     if (banner) {
//       [banner.desktopImage, banner.mobileImage].forEach((url) => {
//         if (url?.startsWith("blob:")) {
//           URL.revokeObjectURL(url);
//           previewUrls.current.delete(url);
//         }
//       });
//     }

//     const index = banners.findIndex((item) => item.id === id);
//     const updated = banners.filter((item) => item.id !== id);

//     setBanners(updated);
//     setActiveIndex(
//       updated.length === 0 ? 0 : Math.min(activeIndex, updated.length - 1),
//     );

//     if (editingId === id) {
//       setEditingId(null);
//     } else if (index < activeIndex) {
//       setActiveIndex((current) => Math.max(0, current - 1));
//     }
//   };

//   const selectBanner = (index) => {
//     setActiveIndex(index);
//   };

//   const scrollCarousel = (direction) => {
//     if (!carouselRef.current) return;

//     carouselRef.current.scrollBy({
//       left: direction * 260,
//       behavior: "smooth",
//     });
//   };

//   const imageForPreview = (banner) => {
//     if (!banner) return "";
//     return previewMobile
//       ? banner.mobileImage || banner.desktopImage
//       : banner.desktopImage || banner.mobileImage;
//   };

//   const UploadBox = ({ banner, type, title }) => {
//     const inputRef = useRef(null);
//     const isDesktop = type === "desktop";
//     const key = isDesktop ? "desktopImage" : "mobileImage";
//     const image = banner[key];

//     return (
//       <div className="min-w-0">
//         <div className="mb-2 flex items-center justify-between gap-2">
//           <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
//             {isDesktop ? <Monitor size={17} /> : <Smartphone size={17} />}
//             {title}
//           </label>

//           {image && (
//             <button
//               type="button"
//               onClick={() => removeImage(banner.id, type)}
//               className="text-xs font-medium text-red-500 hover:text-red-700"
//             >
//               Remove
//             </button>
//           )}
//         </div>

//         <input
//           ref={inputRef}
//           type="file"
//           accept="image/*"
//           className="hidden"
//           onChange={(event) => {
//             selectImage(banner.id, type, event.target.files?.[0]);
//             event.target.value = "";
//           }}
//         />

//         <button
//           type="button"
//           onClick={() => inputRef.current?.click()}
//           className={`group relative flex w-full items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition hover:border-gray-500 hover:bg-gray-100 ${
//             isDesktop ? "aspect-video" : "aspect-[4/5]"
//           }`}
//         >
//           {image ? (
//             <>
//               <img
//                 src={image}
//                 alt={title}
//                 className="absolute inset-0 h-full w-full object-cover"
//               />
//               <span className="absolute inset-0 flex items-center justify-center bg-black/0 text-white opacity-0 transition group-hover:bg-black/40 group-hover:opacity-100">
//                 <span className="rounded-lg bg-black/60 px-3 py-2 text-sm">
//                   Change image
//                 </span>
//               </span>
//             </>
//           ) : (
//             <span className="flex flex-col items-center px-3 text-center">
//               <ImagePlus size={28} className="mb-3 text-gray-400" />
//               <span className="text-sm font-semibold text-gray-700">
//                 Upload {isDesktop ? "desktop" : "mobile"} image
//               </span>
//               <span className="mt-1 text-xs text-gray-400">
//                 JPG, PNG or WebP
//               </span>
//             </span>
//           )}
//         </button>
//       </div>
//     );
//   };

//   return (
//     <div className="flex min-h-screen bg-[#F8F9FA]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       <main
//         className={`w-full min-w-0 p-4 transition-all duration-500 sm:p-6 ${
//           isMobile ? "ml-0" : isSidebarOpen ? "ml-64" : "ml-16"
//         }`}
//       >
//         <Outlet />

//         <div className="mb-7 flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
//           <div className="ml-10 lg:ml-0">
//             <h1 className="text-xl font-bold text-gray-800">
//               Banner Elementor
//             </h1>
//           </div>

//           <div className="flex items-center gap-1">{/*  */}</div>
//         </div>

//         {/* Carousel slide selector */}
//         <section className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
//           <div className="mb-5 flex items-center justify-between gap-3">
//             <div>
//               <h2 className="font-bold text-gray-800">Banner carousel</h2>
//               <p className="mt-1 text-sm text-gray-500">
//                 Select a slide to preview or edit its images and link.
//               </p>
//             </div>

//             <div className="flex shrink-0 items-center gap-2">
//               <button
//                 type="button"
//                 onClick={() => scrollCarousel(-1)}
//                 aria-label="Scroll carousel left"
//                 className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
//               >
//                 <ChevronLeft size={19} />
//               </button>
//               <button
//                 type="button"
//                 onClick={() => scrollCarousel(1)}
//                 aria-label="Scroll carousel right"
//                 className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
//               >
//                 <ChevronRight size={19} />
//               </button>
//             </div>
//           </div>

//           <div
//             ref={carouselRef}
//             className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-3"
//           >
//             {banners.map((banner, index) => {
//               const thumbnail = imageForPreview(banner);
//               const selected = activeBanner?.id === banner.id;

//               return (
//                 <div
//                   key={banner.id}
//                   className={`w-[220px] shrink-0 snap-start overflow-hidden rounded-xl border-2 transition sm:w-[250px] ${
//                     selected
//                       ? "border-gray-900 shadow-md"
//                       : "border-gray-200 hover:border-gray-400"
//                   }`}
//                 >
//                   <button
//                     type="button"
//                     onClick={() => selectBanner(index)}
//                     className="block w-full text-left"
//                   >
//                     <div className="relative flex aspect-video items-center justify-center overflow-hidden bg-gray-100">
//                       {thumbnail ? (
//                         <img
//                           src={thumbnail}
//                           alt={`Banner ${index + 1}`}
//                           className="h-full w-full object-cover"
//                         />
//                       ) : (
//                         <div className="flex flex-col items-center text-gray-400">
//                           <ImagePlus size={27} />
//                           <span className="mt-2 text-xs">No image yet</span>
//                         </div>
//                       )}

//                       <span className="absolute left-2 top-2 rounded-md bg-black/70 px-2 py-1 text-xs font-semibold text-white">
//                         Slide {index + 1}
//                       </span>
//                     </div>

//                     <div className="p-3">
//                       <p className="truncate text-sm font-semibold text-gray-800">
//                         Banner {index + 1}
//                       </p>
//                       <p className="mt-1 text-xs text-gray-500">
//                         {banner.desktopImage ? "Desktop ✓" : "Desktop —"}
//                         {" · "}
//                         {banner.mobileImage ? "Mobile ✓" : "Mobile —"}
//                       </p>
//                     </div>
//                   </button>

//                   <div className="flex gap-2 border-t border-gray-100 p-3">
//                     <button
//                       type="button"
//                       onClick={() => {
//                         setActiveIndex(index);
//                         setEditingId(banner.id);
//                       }}
//                       className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white hover:bg-gray-700"
//                     >
//                       <Pencil size={14} />
//                       Edit slide
//                     </button>

//                     <button
//                       type="button"
//                       onClick={() => deleteBanner(banner.id)}
//                       aria-label={`Delete banner ${index + 1}`}
//                       className="rounded-lg border border-gray-200 p-2 text-red-500 hover:bg-red-50"
//                     >
//                       <Trash2 size={15} />
//                     </button>
//                   </div>
//                 </div>
//               );
//             })}

//             <button
//               type="button"
//               onClick={addBanner}
//               className="flex min-h-[220px] w-[180px] shrink-0 snap-start flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-500 transition hover:border-gray-500 hover:bg-gray-100 sm:w-[200px]"
//             >
//               <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
//                 <Plus size={23} />
//               </span>
//               <span className="text-sm font-semibold">Add banner</span>
//               <span className="mt-1 text-xs">Create a new slide</span>
//             </button>
//           </div>
//         </section>

//         {/* Responsive live preview */}
//         <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
//           <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
//             <div>
//               <h2 className="font-bold text-gray-800">Live Banner Preview</h2>
//               <p className="mt-1 text-sm text-gray-500">
//                 Preview switches to the mobile image when the screen is narrow.
//               </p>
//             </div>

//             <span className="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600">
//               {previewMobile ? <Smartphone size={15} /> : <Monitor size={15} />}
//               {previewMobile ? "Mobile view" : "Desktop view"}
//             </span>
//           </div>

//           {activeBanner ? (
//             <>
//               <div
//                 className={`relative mx-auto overflow-hidden bg-gray-100 ${
//                   previewMobile
//                     ? "h-screen w-full max-w-[360px]"
//                     : "h-screen w-full"
//                 }`}
//               >
//                 {imageForPreview(activeBanner) ? (
//                   <img
//                     src={imageForPreview(activeBanner)}
//                     alt={`Banner ${activeIndex + 1} preview`}
//                     className="absolute inset-0 h-full w-full object-cover"
//                   />
//                 ) : (
//                   <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center text-gray-400">
//                     <ImagePlus size={34} />
//                     <p className="mt-3 text-sm font-semibold">
//                       No {previewMobile ? "mobile" : "desktop"} image selected
//                     </p>
//                     <p className="mt-1 text-xs">
//                       Edit this slide to upload an image.
//                     </p>
//                   </div>
//                 )}

//                 <div className="absolute bottom-3 left-3 rounded-md bg-black/65 px-3 py-1.5 text-xs font-medium text-white">
//                   Slide {activeIndex + 1} / {banners.length}
//                 </div>
//               </div>

//               <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
//                 <p className="min-w-0 break-all text-sm text-gray-500">
//                   <LinkIcon size={15} className="mr-2 inline" />
//                   {activeBanner.link || "No link added"}
//                 </p>

//                 <button
//                   type="button"
//                   onClick={() => setEditingId(activeBanner.id)}
//                   className="flex shrink-0 items-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
//                 >
//                   <Pencil size={15} />
//                   Edit selected slide
//                 </button>
//               </div>
//             </>
//           ) : (
//             <p className="py-12 text-center text-sm text-gray-500">
//               Add a banner to start your carousel.
//             </p>
//           )}
//         </section>
//       </main>

//       {/* Edit slide popup */}
//       {editingBanner && (
//         <div
//           className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-6"
//           onMouseDown={(event) => {
//             if (event.target === event.currentTarget) {
//               setEditingId(null);
//             }
//           }}
//         >
//           <div
//             role="dialog"
//             aria-modal="true"
//             aria-labelledby="edit-banner-title"
//             className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
//           >
//             <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
//               <div>
//                 <h2 id="edit-banner-title" className="font-bold text-gray-800">
//                   Edit banner{" "}
//                   {banners.findIndex(
//                     (banner) => banner.id === editingBanner.id,
//                   ) + 1}
//                 </h2>
//                 <p className="mt-1 text-xs text-gray-500">
//                   Images and link apply only to this slide.
//                 </p>
//               </div>

//               <button
//                 type="button"
//                 onClick={() => setEditingId(null)}
//                 aria-label="Close editor"
//                 className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
//               >
//                 <X size={21} />
//               </button>
//             </div>

//             <div className="overflow-y-auto p-5 sm:p-6">
//               <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
//                 <UploadBox
//                   key={`${editingBanner.id}-desktop`}
//                   banner={editingBanner}
//                   type="desktop"
//                   title="Desktop image"
//                 />

//                 <UploadBox
//                   key={`${editingBanner.id}-mobile`}
//                   banner={editingBanner}
//                   type="mobile"
//                   title="Mobile image"
//                 />
//               </div>

//               <div className="mt-6">
//                 <label
//                   htmlFor="banner-destination"
//                   className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700"
//                 >
//                   <LinkIcon size={16} />
//                   Link
//                 </label>

//                 <input
//                   id="banner-destination"
//                   type="text"
//                   value={editingBanner.link}
//                   onChange={(event) =>
//                     updateBanner(editingBanner.id, {
//                       link: event.target.value,
//                     })
//                   }
//                   placeholder="/property-details/..."
//                   className="w-full rounded-lg border border-gray-300 px-3 py-3 text-sm outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100"
//                 />

//                 <p className="mt-2 text-xs text-gray-400">
//                   Enter the page path or URL users should visit when this banner
//                   is clicked.
//                 </p>
//               </div>

//               <div className="mt-6 rounded-xl bg-gray-50 p-4">
//                 <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
//                   <Eye size={16} />
//                   Preview for this slide
//                 </div>

//                 <div
//                   className={`relative mx-auto overflow-hidden rounded-lg bg-gray-200 ${
//                     previewMobile
//                       ? "h-[70vh] w-full max-w-[280px]"
//                       : "h-[70vh] w-full"
//                   }`}
//                 >
//                   {imageForPreview(editingBanner) ? (
//                     <img
//                       src={imageForPreview(editingBanner)}
//                       alt="Selected banner"
//                       className="absolute inset-0 h-full w-full object-cover"
//                     />
//                   ) : (
//                     <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
//                       {previewMobile ? (
//                         <Smartphone size={24} />
//                       ) : (
//                         <Monitor size={24} />
//                       )}
//                       <span className="mt-2 text-xs">
//                         Select an image to preview
//                       </span>
//                     </div>
//                   )}
//                 </div>

//                 <p className="mt-3 text-xs text-gray-500">
//                   Current preview: {previewMobile ? "Mobile" : "Desktop"}.
//                   Resize the browser to check the responsive image.
//                 </p>
//               </div>
//             </div>

//             <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4 sm:px-6">
//               <button
//                 type="button"
//                 onClick={() => setEditingId(null)}
//                 className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
//               >
//                 Close
//               </button>

//               <button
//                 type="button"
//                 onClick={() => setEditingId(null)}
//                 className="rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-700"
//               >
//                 Done
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }
